/**
 * Shortlist scoring — ranks (site, investigator) pairs for a trial profile.
 *
 * This is the NSCLC top-250 methodology generalized: the method is fixed, the
 * target profile and the pillar weights are parameters. Nothing here is a
 * stored ranking — every score is computed from component metrics at query
 * time, so the published list falls out of running the method on the original
 * profile rather than being replayed.
 *
 *   1. Comparable units. Site performance arrives as DQS *index* metrics —
 *      each already normalised against the median site on the same trial —
 *      direction-adjusted (lower-is-better metrics inverted) and then turned
 *      into a within-cohort percentile.
 *   2. Relevance. Every trial observation is weighted by how well that trial
 *      matches the target profile (TA, indication, phase, line). The original
 *      Ph3/1L ladder is one instance of this general rule.
 *   3. Evidence shrinkage. Percentiles are pulled toward the median by how
 *      much relevant evidence backs them (empirical-Bayes, K=2), so a
 *      one-trial fluke cannot outrank a consistent performer.
 *   4. Weighted pillars. Pillar scores combine into one composite using
 *      user-configurable weights, defaulting to the published set.
 */
import type { InvestigatorProfile } from "./components/InvestigatorProfileDrawer";
import { currentSite } from "./plannedSites";

export type PillarGroup = "site" | "investigator";

export interface PillarDef {
  key: string;
  label: string;
  group: PillarGroup;
  description: string;
}

/** The published pillar set. Site pillars come from site performance metrics;
 *  investigator pillars from the trial history of the pair's investigator. */
export const PILLARS: PillarDef[] = [
  {
    key: "enrollment_output",
    label: "Enrollment output",
    group: "site",
    description: "Patients enrolled versus the median site on the same trials.",
  },
  {
    key: "enrollment_rate",
    label: "Enrollment rate",
    group: "site",
    description: "Patients per site per month versus same-trial peers.",
  },
  {
    key: "startup_speed",
    label: "Startup speed",
    group: "site",
    description:
      "Site activation and IRB cycle times — the most repeatable operational signal.",
  },
  {
    key: "screening_efficiency",
    label: "Screening efficiency",
    group: "site",
    description: "Screen-failure rate relative to same-trial peers.",
  },
  {
    key: "retention_quality",
    label: "Retention quality",
    group: "site",
    description:
      "Completion rate. Down-weighted by default — it repeats weakly across trials.",
  },
  {
    key: "reliability_experience",
    label: "Reliability & experience",
    group: "investigator",
    description:
      "Breadth of relevant trial history, consistency of enrolling, and investigator depth.",
  },
  {
    key: "investigator_depth",
    label: "Investigator depth",
    group: "investigator",
    description:
      "PI-specific depth (trial count, patients delivered, years). Weighted 0 by default: we cannot yet compute it from PI-role data, and approximating it measurably degraded agreement with the published ranking. Raise it once a PI-level feed is wired.",
  },
  {
    key: "profile_fit",
    label: "Profile fit",
    group: "investigator",
    description:
      "How much of this pair's history matches the selected TA, indication, phase and line.",
  },
];

export type ShortlistWeights = Record<string, number>;

/** The weights published with the NSCLC deliverable, reliability-informed. */
export const DEFAULT_WEIGHTS: ShortlistWeights = {
  enrollment_output: 24,
  enrollment_rate: 14,
  startup_speed: 22,
  screening_efficiency: 9,
  retention_quality: 5,
  reliability_experience: 16,
  investigator_depth: 0,
  profile_fit: 10,
};

export const WEIGHT_PRESETS: Record<string, ShortlistWeights> = {
  Default: { ...DEFAULT_WEIGHTS },
  Balanced: {
    enrollment_output: 15,
    enrollment_rate: 14,
    startup_speed: 14,
    screening_efficiency: 14,
    retention_quality: 14,
    reliability_experience: 15,
    investigator_depth: 0,
    profile_fit: 14,
  },
  "Enrollment-heavy": {
    enrollment_output: 34,
    enrollment_rate: 24,
    startup_speed: 12,
    screening_efficiency: 8,
    retention_quality: 4,
    reliability_experience: 12,
    investigator_depth: 0,
    profile_fit: 6,
  },
  "Startup-heavy": {
    enrollment_output: 16,
    enrollment_rate: 10,
    startup_speed: 36,
    screening_efficiency: 12,
    retention_quality: 4,
    reliability_experience: 14,
    investigator_depth: 0,
    profile_fit: 8,
  },
  "Experience-heavy": {
    enrollment_output: 14,
    enrollment_rate: 10,
    startup_speed: 12,
    screening_efficiency: 6,
    retention_quality: 4,
    reliability_experience: 30,
    investigator_depth: 12,
    profile_fit: 24,
  },
};

export interface ShortlistProfile {
  ta: string;
  indication: string;
  phase: string;
  line: string;
  regions: string[];
}

/**
 * One raw per-(site, trial) metrics row from
 * GET /data/portfolio/site-trial-metrics/. These are the same six quantities
 * the published pipeline scored (enrolled, patients/site/month, start-up
 * months, IRB cycle months, % screen fail, % complete) — unnormalised, so the
 * method can do its own within-trial comparison rather than inherit someone
 * else's.
 */
export interface SiteTrialMetricRow {
  golden_id?: string | null;
  bare_id?: string | null;
  trial_nct_id: string;
  phase?: string | null;
  indication?: string | null;
  therapeutic_area?: string | null;
  /** Mastered line of therapy: "1L", "2L+", or null/"NA" when unknown. */
  line_of_therapy?: string | null;
  n_enrolled?: number | null;
  ppsm?: number | null;
  startup_time?: number | null;
  irb_cycle_time?: number | null;
  pct_screen_fail?: number | null;
  pct_complete?: number | null;
  [key: string]: unknown;
}

/** The published PERF_METRICS, with the direction each is good in. */
const PERF_METRICS: Array<{ key: string; higherIsBetter: boolean }> = [
  { key: "n_enrolled", higherIsBetter: true },
  { key: "ppsm", higherIsBetter: true },
  { key: "startup_time", higherIsBetter: false },
  { key: "irb_cycle_time", higherIsBetter: false },
  { key: "pct_screen_fail", higherIsBetter: false },
  { key: "pct_complete", higherIsBetter: true },
];

/** Site pillars composed exactly as score_sites.py composes them. */
const SITE_PILLAR_METRICS: Record<string, Array<[string, number]>> = {
  enrollment_output: [
    ["n_enrolled", 0.5],
    ["ppsm", 0.5],
  ],
  enrollment_rate: [["ppsm", 1]],
  startup_speed: [
    ["startup_time", 0.65],
    ["irb_cycle_time", 0.35],
  ],
  screening_efficiency: [["pct_screen_fail", 1]],
  retention_quality: [["pct_complete", 1]],
};

export interface ShortlistPair {
  inv_key: string;
  inv: InvestigatorProfile;
  site_name: string;
  site_key?: string;
  city: string;
  country: string;
  region: string;
  pillars: Record<string, number>;
  /** Relevance-weighted evidence behind the investigator-side pillars. */
  n_evidence: number;
  n_profile_trials: number;
  n_total_trials: number;
  /** True when site performance metrics were found for this pair's site. */
  has_site_metrics: boolean;
  evidence: {
    total_enrolled: number;
    n_trials: number;
    n_phase_trials: number;
    years_experience: number;
    patients_per_month: number | null;
  };
}

export interface ScoredPair extends ShortlistPair {
  score: number;
  rank: number;
}

// ---- fixed methodology constants ------------------------------------------
const SHRINK_K = 2;
const PRIOR = 0.5;
/** Floor on relevance so an off-profile trial still counts a little, as in
 *  the published ladder (Ph3/1L = 1.0 down to early phase = 0.3). */
const RELEVANCE_FLOOR = 0.3;

const shrink = (pctile: number, evidence: number): number =>
  (evidence * pctile + SHRINK_K * PRIOR) / (evidence + SHRINK_K);

/** Fraction of the cohort below the value (mid-ranked for ties). */
function percentiles(values: Array<number | null>): number[] {
  const present = values.filter((v): v is number => v != null);
  const sorted = [...present].sort((a, b) => a - b);
  const n = sorted.length;
  if (n <= 1) return values.map(() => PRIOR);
  return values.map((v) => {
    if (v == null) return PRIOR;
    let lo = 0;
    let hi = n;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sorted[mid] < v) lo = mid + 1;
      else hi = mid;
    }
    let hi2 = lo;
    while (hi2 < n && sorted[hi2] === v) hi2++;
    return (lo + hi2) / 2 / n;
  });
}

function norm01(values: number[]): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const lo = sorted[Math.floor(sorted.length * 0.05)] ?? 0;
  const hi = sorted[Math.floor(sorted.length * 0.95)] ?? 1;
  if (!(hi > lo)) return values.map(() => PRIOR);
  return values.map((v) => Math.min(1, Math.max(0, (v - lo) / (hi - lo))));
}

const phaseDigits = (phase: string | null | undefined): string =>
  (phase ?? "").replace(/[^0-9/]/g, "");

export function matchesPhase(
  trialPhase: string | null,
  targetPhase: string,
): boolean {
  const target = phaseDigits(targetPhase);
  if (!target) return false;
  return phaseDigits(trialPhase).split("/").includes(target.split("/")[0]);
}

type TrialEntry = InvestigatorProfile["trials"][number];

const escapeRegExp = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function mentions(trial: RelevanceSubject, term: string): boolean {
  if (!term) return true;
  const text = trial.indication ?? trial.trial_name ?? "";
  return new RegExp(escapeRegExp(term), "i").test(text);
}

const LINE_PATTERNS: Record<string, RegExp> = {
  "1L": /first[\s-]?line|\b1L\b|treatment[\s-]?naive|previously untreated/i,
  "2L+":
    /second[\s-]?line|\b2L\b|\b3L\b|later[\s-]?line|relapsed|refractory|previously treated/i,
};

/**
 * Line-of-therapy agreement. A mastered `line_of_therapy` is authoritative;
 * only when it is absent do we fall back to reading the trial title, which is
 * why promoting this attribute matters — the published ladder separates
 * Ph3/1L (1.0) from Ph3/2L (0.7) and Ph2/1L (0.6) from Ph2/2L (0.4), and that
 * split is invisible without it.
 */
function lineFactor(trial: RelevanceSubject, wantedLine: string): number {
  const mastered = (trial.line_of_therapy ?? "").trim();
  if (mastered && mastered.toUpperCase() !== "NA") {
    return mastered.toUpperCase() === wantedLine.toUpperCase() ? 1 : 0.6;
  }
  const wanted = LINE_PATTERNS[wantedLine];
  const other = Object.entries(LINE_PATTERNS).find(
    ([key]) => key !== wantedLine,
  )?.[1];
  const name = trial.trial_name ?? "";
  if (wanted?.test(name)) return 1;
  if (other?.test(name)) return 0.6;
  return 0.85;
}

/**
 * How well one trial matches the target profile, in [FLOOR, 1].
 *
 * The published model hand-set this ladder for its single profile (Ph3/1L =
 * 1.0 … early phase = 0.3). Here each profile dimension contributes a factor
 * and they multiply, so the same rule generalises to any TA/indication/phase/
 * line while reproducing that ladder on the original profile. Dimensions the
 * user left unset contribute nothing; dimensions we cannot read off the trial
 * count as "unknown" rather than "mismatch", so a thin record is not punished
 * as hard as a wrong one.
 */
interface RelevanceSubject {
  phase?: string | null;
  indication?: string | null;
  line_of_therapy?: string | null;
  trial_name?: string | null;
}

export function relevance(
  trial: RelevanceSubject,
  profile: ShortlistProfile,
): number {
  let factor = 1;

  if (profile.phase) {
    const trialPhase = phaseDigits(trial.phase);
    if (!trialPhase) factor *= 0.85;
    else if (matchesPhase(trial.phase ?? null, profile.phase)) factor *= 1;
    else {
      const target = Number(phaseDigits(profile.phase).split("/")[0]);
      const nearest = Math.min(
        ...trialPhase
          .split("/")
          .map((p) => Math.abs(Number(p) - target))
          .filter((d) => Number.isFinite(d)),
      );
      factor *= Number.isFinite(nearest) && nearest <= 1 ? 0.8 : 0.5;
    }
  }

  if (profile.indication) {
    // A mastered indication beats guessing from the trial title.
    const text = trial.indication ?? trial.trial_name ?? "";
    if (!text) factor *= 0.85;
    else if (new RegExp(escapeRegExp(profile.indication), "i").test(text))
      factor *= 1;
    else factor *= 0.7;
  }

  if (profile.line) {
    factor *= lineFactor(trial, profile.line);
  }

  return Math.max(RELEVANCE_FLOOR, factor);
}

function monthsBetween(start: string, end: string | null): number | null {
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;
  return (e - s) / (1000 * 60 * 60 * 24 * 30.44);
}

function medianPatientsPerMonth(trials: TrialEntry[]): number | null {
  const rates = trials
    .map((t) => {
      if (t.enrolled == null || t.enrolled <= 0 || !t.tenure_start) return null;
      const months = monthsBetween(t.tenure_start, t.tenure_end ?? null);
      return months && months >= 1 ? t.enrolled / months : null;
    })
    .filter((r): r is number => r != null)
    .sort((a, b) => a - b);
  if (!rates.length) return null;
  const mid = rates.length >> 1;
  return rates.length % 2 ? rates[mid] : (rates[mid - 1] + rates[mid]) / 2;
}

/** Golden ids appear both prefixed (GSID-TKD-DD…) and bare; match on both. */
const bareId = (key: string): string =>
  key.replace(/^GSID-[A-Z0-9]+-/i, "").toLowerCase();

/**
 * Within-trial percentiles for every raw metric: for each trial, rank its
 * sites against each other (direction-adjusted). This is the published
 * comparability step — a site is judged against the peers who ran the same
 * protocol, not against the whole portfolio.
 */
function withinTrialPercentiles(
  rows: SiteTrialMetricRow[],
): Map<SiteTrialMetricRow, Record<string, number>> {
  const byTrial = new Map<string, SiteTrialMetricRow[]>();
  for (const row of rows) {
    const list = byTrial.get(row.trial_nct_id) ?? [];
    list.push(row);
    byTrial.set(row.trial_nct_id, list);
  }
  const out = new Map<SiteTrialMetricRow, Record<string, number>>();
  for (const trialRows of byTrial.values()) {
    for (const { key, higherIsBetter } of PERF_METRICS) {
      const values = trialRows.map((r) => {
        const v = r[key];
        return typeof v === "number" ? (higherIsBetter ? v : -v) : null;
      });
      const pct = percentiles(values);
      trialRows.forEach((row, i) => {
        if (values[i] == null) return;
        const acc = out.get(row) ?? {};
        acc[key] = pct[i];
        out.set(row, acc);
      });
    }
  }
  return out;
}

/**
 * Build scoreable pairs. Site pillars are computed from raw per-(site, trial)
 * metrics: within-trial percentile, relevance-weighted mean across the site's
 * trials, then shrunk by the relevance-weighted evidence behind each metric —
 * the published method, executed here rather than inherited.
 */
export function buildPairs(
  investigators: InvestigatorProfile[],
  profile: ShortlistProfile,
  siteTrialRows?: SiteTrialMetricRow[],
): { pairs: ShortlistPair[]; excluded: number } {
  const inScope = profile.regions.length
    ? investigators.filter((inv) => profile.regions.includes(inv.region ?? ""))
    : investigators;

  // Relevance-weighted aggregate of within-trial percentiles, per site, plus
  // the site's own trial counts — the published fit pillar is defined on the
  // site's trials, and those rows carry mastered phase/indication/line.
  const siteAgg = new Map<
    string,
    Record<string, { num: number; den: number }>
  >();
  const siteFit = new Map<string, { total: number; matched: number }>();
  if (siteTrialRows?.length) {
    const pctByRow = withinTrialPercentiles(siteTrialRows);
    for (const row of siteTrialRows) {
      const pct = pctByRow.get(row);
      if (!pct) continue;
      const weight = relevance(row, profile);
      const onProfile =
        (!profile.phase || matchesPhase(row.phase ?? null, profile.phase)) &&
        mentions(row, profile.indication) &&
        (!profile.line || lineFactor(row, profile.line) === 1);
      for (const id of [row.golden_id, row.bare_id]) {
        if (!id) continue;
        const key = String(id).toLowerCase();
        const fit = siteFit.get(key) ?? { total: 0, matched: 0 };
        fit.total += 1;
        if (onProfile) fit.matched += 1;
        siteFit.set(key, fit);
        const acc = siteAgg.get(key) ?? {};
        for (const { key: metric } of PERF_METRICS) {
          const p = pct[metric];
          if (p == null) continue;
          const cell = acc[metric] ?? { num: 0, den: 0 };
          cell.num += weight * p;
          cell.den += weight;
          acc[metric] = cell;
        }
        siteAgg.set(key, acc);
      }
    }
  }

  const aggFor = (
    siteKey: string | undefined,
  ): Record<string, { num: number; den: number }> | undefined => {
    if (!siteKey) return undefined;
    return siteAgg.get(siteKey.toLowerCase()) ?? siteAgg.get(bareId(siteKey));
  };
  const fitFor = (
    siteKey: string | undefined,
  ): { total: number; matched: number } | undefined => {
    if (!siteKey) return undefined;
    return siteFit.get(siteKey.toLowerCase()) ?? siteFit.get(bareId(siteKey));
  };

  interface Raw {
    inv: InvestigatorProfile;
    site: ReturnType<typeof currentSite>;
    agg?: Record<string, { num: number; den: number }>;
    siteFit?: { total: number; matched: number };
    effN: number;
    nTrials: number;
    nProfile: number;
    nPhase: number;
    consistency: number;
    invDepth: number;
    years: number;
    invEnrolled: number;
    ppm: number | null;
  }

  const raws: Raw[] = [];
  let excluded = 0;

  for (const inv of inScope) {
    const trials = inv.trials ?? [];
    const site = currentSite(inv);
    const agg = aggFor(site?.key);
    const withEnrollment = trials.filter((t) => t.enrolled != null);

    // Evidence gate: some record of delivering patients, from either side.
    if (!withEnrollment.length && !inv.total_enrolled && !agg) {
      excluded++;
      continue;
    }

    const relSum = trials.reduce((sum, t) => sum + relevance(t, profile), 0);
    const nTrials = inv.trial_count ?? trials.length;
    const nPhase = profile.phase
      ? trials.filter((t) => matchesPhase(t.phase, profile.phase)).length
      : nTrials;
    const matchesProfile = (t: TrialEntry): boolean =>
      (!profile.phase || matchesPhase(t.phase, profile.phase)) &&
      mentions(t, profile.indication) &&
      (!profile.line ||
        Boolean(LINE_PATTERNS[profile.line]?.test(t.trial_name ?? "")));
    const taMatch =
      !profile.ta || (inv.therapeutic_areas ?? []).includes(profile.ta);
    const nProfile = taMatch ? trials.filter(matchesProfile).length : 0;
    const nonZero = withEnrollment.filter((t) => (t.enrolled ?? 0) > 0).length;

    raws.push({
      inv,
      site,
      agg,
      siteFit: fitFor(site?.key),
      effN: relSum,
      nTrials,
      nProfile,
      nPhase,
      consistency: withEnrollment.length
        ? nonZero / withEnrollment.length
        : PRIOR,
      invDepth: Math.log1p(nTrials),
      years: inv.years_experience ?? 0,
      invEnrolled: inv.total_enrolled ?? 0,
      ppm: medianPatientsPerMonth(trials),
    });
  }

  // ---- site pillars from the relevance-weighted, shrunk metric scores
  const metricScore = (raw: Raw, metric: string): number | null => {
    const cell = raw.agg?.[metric];
    if (!cell || cell.den === 0) return null;
    return shrink(cell.num / cell.den, cell.den);
  };
  const sitePillarScores = new Map<string, number[]>();
  for (const [pillar, comps] of Object.entries(SITE_PILLAR_METRICS)) {
    sitePillarScores.set(
      pillar,
      raws.map((raw) => {
        let num = 0;
        let den = 0;
        for (const [metric, weight] of comps) {
          const v = metricScore(raw, metric);
          if (v != null) {
            num += weight * v;
            den += weight;
          }
        }
        return den > 0 ? num / den : PRIOR;
      }),
    );
  }

  // ---- investigator pillars
  const breadth = norm01(raws.map((r) => Math.log1p(r.effN)));
  const depth = norm01(raws.map((r) => r.invDepth));
  const yearsPct = percentiles(raws.map((r) => r.years));
  const enrolledPct = percentiles(raws.map((r) => Math.log1p(r.invEnrolled)));
  // Profile fit generalises the published phase3/1L fit: how concentrated the
  // pair's history is on the target profile, plus how much of it there is.
  // Prefer the site's own mastered trial mix; fall back to the investigator's
  // directory history when no site rows were served.
  const fitCounts = raws.map((r) =>
    r.siteFit && r.siteFit.total > 0
      ? { matched: r.siteFit.matched, total: r.siteFit.total }
      : { matched: r.nProfile, total: r.nTrials },
  );
  const fitFrac = fitCounts.map((f) =>
    f.total > 0 ? Math.min(1, f.matched / f.total) : 0,
  );
  const fitBreadth = norm01(fitCounts.map((f) => Math.log1p(f.matched)));

  const pairs = raws.map((r, i): ShortlistPair => {
    const pillars: Record<string, number> = {};
    for (const pillar of Object.keys(SITE_PILLAR_METRICS)) {
      pillars[pillar] = sitePillarScores.get(pillar)?.[i] ?? PRIOR;
    }
    // Reliability keeps only the two terms we can compute faithfully —
    // relevant trial breadth and enrolling consistency — renormalized to the
    // published 0.40/0.28 ratio.
    pillars.reliability_experience = shrink(
      0.59 * breadth[i] + 0.41 * r.consistency,
      r.effN,
    );
    // The PI-flavoured terms live in their own pillar, weighted 0 by default.
    // Approximating them from directory data measurably worsened agreement
    // with the published ranking, so they are opt-in rather than baked in.
    pillars.investigator_depth = shrink(
      0.55 * depth[i] + 0.3 * enrolledPct[i] + 0.15 * yearsPct[i],
      r.effN,
    );
    pillars.profile_fit = shrink(
      0.6 * fitFrac[i] + 0.4 * fitBreadth[i],
      r.effN,
    );

    return {
      inv_key: r.inv.inv_key,
      inv: r.inv,
      site_name: r.site?.name ?? "",
      site_key: r.site?.key,
      city: r.site?.city || r.inv.city,
      country: r.site?.country || r.inv.country,
      region: r.inv.region ?? "",
      pillars,
      n_evidence: r.effN,
      n_profile_trials: r.nProfile,
      n_total_trials: r.nTrials,
      has_site_metrics: Boolean(r.agg),
      evidence: {
        total_enrolled: r.invEnrolled,
        n_trials: r.nTrials,
        n_phase_trials: r.nPhase,
        years_experience: r.years,
        patients_per_month: r.ppm,
      },
    };
  });

  return { pairs, excluded };
}

export function normalizeWeights(
  weights: ShortlistWeights,
  defs: PillarDef[] = PILLARS,
): Record<string, number> {
  const total = defs.reduce((sum, p) => sum + (weights[p.key] || 0), 0) || 1;
  return Object.fromEntries(
    defs.map((p) => [p.key, (weights[p.key] || 0) / total]),
  );
}

/** Composite and rank. Only this step re-runs when a weight slider moves. */
export function scorePairs(
  pairs: ShortlistPair[],
  weights: ShortlistWeights,
  defs: PillarDef[] = PILLARS,
): ScoredPair[] {
  const w = normalizeWeights(weights, defs);
  return pairs
    .map((pair) => ({
      ...pair,
      score:
        defs.reduce(
          (sum, p) => sum + w[p.key] * (pair.pillars[p.key] ?? PRIOR),
          0,
        ) * 100,
      rank: 0,
    }))
    .sort((a, b) => b.score - a.score)
    .map((pair, i) => ({ ...pair, rank: i + 1 }));
}

export function groupScores(
  pair: ShortlistPair,
  weights: ShortlistWeights,
  defs: PillarDef[] = PILLARS,
): Record<PillarGroup, number> {
  const w = normalizeWeights(weights, defs);
  const roll = (group: PillarGroup): number => {
    let num = 0;
    let den = 0;
    for (const p of defs.filter((pl) => pl.group === group)) {
      num += w[p.key] * (pair.pillars[p.key] ?? PRIOR);
      den += w[p.key];
    }
    return den > 0 ? (num / den) * 100 : 0;
  };
  return { site: roll("site"), investigator: roll("investigator") };
}

/** Collapse to the best-scoring pair per site ("group by site" view). */
export function bestPairPerSite(pairs: ScoredPair[]): ScoredPair[] {
  const seen = new Set<string>();
  return pairs.filter((p) => {
    const key = p.site_name || `inv:${p.inv_key}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
